/*
 * Copyright (c) 2023. American Express Company
 */

// theirs
import _ from 'lodash';
import React, { useCallback } from 'react';
import { Alert, FieldControl, Input, Label } from '@americanexpress/dls-react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField, useFormikContext } from 'formik';

// ours
import { Ticker } from 'src/types/investment-holding.model';
import InvestmentHoldingService from 'src/services/investment-holding.service';
import { TickerInputViewModel } from 'src/types/view-models/ticker-input.view.model';
import { InvestmentDrawerFormValues } from 'src/types/form-models/investment-drawer-form-values.model';

export default function TickerInput({ id, label }: TickerInputViewModel): JSX.Element {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [field, meta, helpers]: [FieldInputProps<string>, FieldMetaProps<string>, FieldHelperProps<string>] =
        useField(id);
    const { setFieldValue } = useFormikContext<InvestmentDrawerFormValues>();
    const hasError: boolean = meta.touched && meta.error?.length > 0;
    const { value }: { value: string } = meta;
    const { setValue }: { setValue: (value: string) => void } = helpers;
    // DLS requires we associate Alert with Input in this particular way
    const alertId: string = `${id}Alert`;
    const describedBy: { describedBy?: string } = hasError ? { describedBy: alertId } : {};

    const handleChange = (value: string): void => {
        setValue(value);
        void debouncedLookupTicker(value);
    };

    const debouncedLookupTicker = useCallback(
        _.debounce(async (value) => {
            if (value.length > 2) {
                const ticker: Ticker = await InvestmentHoldingService.getTicker(value);
                if (ticker) {
                    setValue(ticker.ticker);
                    setFieldValue('name', ticker.name);
                    setFieldValue('price', ticker.price);
                    setFieldValue('lastUpdated', ticker.lastUpdated);
                }
            }
        }, 1000),
        []
    );

    return (
        <FieldControl status={hasError ? 'error' : 'default'} data-cy="tickerInput">
            <Label htmlFor={id}>{label}</Label>
            <Input
                id={id}
                // set value and onChange manually so that we can trigger ticker lookup
                value={value}
                onChange={(e: any) => handleChange(e.target.value)}
                {...describedBy}
            />
            {hasError && <Alert id={alertId}>{meta.error}</Alert>}
        </FieldControl>
    );
}
